import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

import { AnimatedModal } from '@repo/ui/components/Framer/AnimatedModal/AnimatedModal';
import { useDeviceType } from '@repo/ui/hooks/use-device-type';

import './FilterDropdownPanel.scss';

interface FilterDropdownPanelProps {
  isOpen: boolean;
  /** Non-scrolling content rendered above the scrollable body, e.g. a search input. */
  header?: ReactNode;
  /** Scrollable option list. */
  children: ReactNode;
  className?: string;
  maxHeight?: number | string;
  dataTestId?: string;
  /**
   * When the child owns its own scroll container (e.g. it virtualizes its own list), set this
   * so the body doesn't also impose overflow/max-height - avoiding a nested scrollbar. The
   * `--filter-dropdown-panel-max-height` CSS var is still set for the child to read.
   */
  bodyManagesOwnScroll?: boolean;
  /** Presents as a full-width slide-up sheet on mobile instead of an anchored dropdown. */
  mobileBottomSheet?: boolean;
}

export function FilterDropdownPanel({
  isOpen,
  header,
  children,
  className,
  maxHeight = 320,
  dataTestId,
  bodyManagesOwnScroll = false,
  mobileBottomSheet = false,
}: FilterDropdownPanelProps) {
  const { isMobile } = useDeviceType();
  const bodyStyle = {
    '--filter-dropdown-panel-max-height': typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
  } as CSSProperties;
  const showAsBottomSheet = mobileBottomSheet && isMobile;

  return (
    <AnimatedModal
      isOpen={isOpen}
      className={clsx('FilterDropdownPanel', { 'FilterDropdownPanel--bottom-sheet': showAsBottomSheet }, className)}
      dataTestId={dataTestId}
      variant={showAsBottomSheet ? 'bottomSheet' : 'dropdown'}
    >
      {header && <div className="FilterDropdownPanel__header">{header}</div>}
      <div
        className={clsx('FilterDropdownPanel__body', { 'FilterDropdownPanel__body--managed': bodyManagesOwnScroll })}
        style={bodyStyle}
      >
        {children}
      </div>
    </AnimatedModal>
  );
}
